/**
 * Droit d'accès et droit à l'effacement (RGPD, docs/20 §7).
 *
 * Deux gestes de nature très différente dans une même carte, et c'est voulu :
 * ce sont les deux droits que l'utilisateur exerce sur l'ensemble de ses
 * données, et les chercher à deux endroits séparés reviendrait à les cacher.
 *
 * La suppression demande une **confirmation explicite** — retaper son adresse —
 * avant même que le serveur ne réclame une authentification récente. Un bouton
 * qui déclenche un effacement au premier clic n'est pas une interface, c'est un
 * piège ; et la confirmation par saisie oblige à lire ce qu'on s'apprête à faire.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCancelAccountDeletion,
  useExportMyData,
  useRequestAccountDeletion,
  type Account,
} from "@/features/account/api/useAccount";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";

interface DangerZoneProps {
  account: Account;
  formatDate: (value: string) => string;
}

export function DangerZone({ account, formatDate }: DangerZoneProps) {
  const { t } = useTranslation("account");
  const errorMessage = useApiErrorMessage();
  const exportData = useExportMyData();
  const requestDeletion = useRequestAccountDeletion();
  const cancelDeletion = useCancelAccountDeletion();

  const [confirming, setConfirming] = useState(false);
  const [typedEmail, setTypedEmail] = useState("");

  const pending = account.deletion_scheduled_for !== null;
  // Comparaison insensible à la casse et aux blancs de bord : refuser
  // « Camille@Example.fr » ne protège personne et n'apprend rien à qui l'a tapé.
  const confirmed = typedEmail.trim().toLowerCase() === account.email.toLowerCase();

  const closeConfirmation = () => {
    setConfirming(false);
    setTypedEmail("");
  };

  return (
    <Card title={t("privacy.heading")}>
      <div className="space-y-6">
        <section className="space-y-2">
          <h3 className="text-sm font-medium">{t("privacy.export.heading")}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t("privacy.export.description")}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              exportData.mutate();
            }}
            disabled={exportData.isPending}
          >
            {exportData.isPending ? t("privacy.export.pending") : t("privacy.export.action")}
          </Button>
          {exportData.isError && <Alert tone="error">{errorMessage(exportData.error)}</Alert>}
          {exportData.isSuccess && <Alert tone="success">{t("privacy.export.done")}</Alert>}
        </section>

        <hr className="border-slate-200 dark:border-slate-800" />

        {pending ? (
          <section className="space-y-3">
            <Alert tone="warning">
              {t("privacy.deletion.scheduled", {
                date: formatDate(account.deletion_scheduled_for ?? ""),
              })}
            </Alert>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("privacy.deletion.stillUsable")}
            </p>
            <Button
              onClick={() => {
                cancelDeletion.mutate();
              }}
              disabled={cancelDeletion.isPending}
            >
              {t("privacy.deletion.cancel")}
            </Button>
            {cancelDeletion.isError && (
              <Alert tone="error">{errorMessage(cancelDeletion.error)}</Alert>
            )}
          </section>
        ) : (
          <section className="space-y-3">
            <h3 className="text-sm font-medium text-red-700 dark:text-red-400">
              {t("privacy.deletion.heading")}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("privacy.deletion.description")}
            </p>

            {confirming ? (
              <div className="space-y-3 rounded-md border border-red-300 p-4 dark:border-red-900">
                <label htmlFor="delete-confirm" className="block text-sm font-medium">
                  {t("privacy.deletion.confirmLabel", { email: account.email })}
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  autoComplete="off"
                  value={typedEmail}
                  onChange={(event) => {
                    setTypedEmail(event.target.value);
                  }}
                  className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm sm:w-96 dark:border-slate-700 dark:bg-slate-900"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="danger"
                    disabled={!confirmed || requestDeletion.isPending}
                    onClick={() => {
                      requestDeletion.mutate(undefined, { onSuccess: closeConfirmation });
                    }}
                  >
                    {t("privacy.deletion.confirm")}
                  </Button>
                  <Button variant="ghost" onClick={closeConfirmation}>
                    {t("privacy.deletion.abort")}
                  </Button>
                </div>
                {requestDeletion.isError && (
                  <Alert tone="error">{errorMessage(requestDeletion.error)}</Alert>
                )}
              </div>
            ) : (
              <Button
                variant="danger"
                onClick={() => {
                  setConfirming(true);
                }}
              >
                {t("privacy.deletion.start")}
              </Button>
            )}
          </section>
        )}
      </div>
    </Card>
  );
}
